import { useState, useRef, useEffect } from 'react';
import { Shirt, Sun, Waves, Sparkles, ExternalLink } from 'lucide-react';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/7.png`;
const PINTEREST_IDEAS_DAMAS = 'https://mx.pinterest.com/samanthaislasru/wedding/ideas-inspo-invitadas/';
const PINTEREST_IDEAS_CABALLEROS = 'https://www.pinterest.com/samanthaislasru/wedding/ideas-inspo-invitados/';

const SECTION_OVERLAY = 'rgba(252, 235, 208, 0.6)';
const CARD_BG = 'rgba(252, 235, 208, 0.75)';

const PALETTE_SWATCHES = [
  '#F2826C', // Coral
  '#F6D871', // Yellow
  '#F5AA83', // Peach
  '#E5ACBF', // Pink
  '#92ADA4', // Eucalyptus
  '#F2D6A1', // Lemonade
  '#F1A805', // Limoncello
  '#5D768B', // Ocean Deep Blue
  '#C8B39B', // Warm Sandy Beige
  '#E3C9A4', // Golden Driftwood
  '#1d3b28', // Emerald
];

export function DressCode() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const enterStart = vh * 0.7;
      const enterEnd = -vh * 0.15;
      const progress = Math.max(0, Math.min(1, (enterStart - rect.top) / (enterStart - enterEnd)));
      setScrollProgress(progress);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dressItems = [
    { icon: Shirt, title: 'Formal de Playa', description: 'Elegante pero cómodo', examples: ['Vestidos ligeros', 'Guayaberas', 'Lino y algodón'] },
    { icon: Sun, title: 'Colores Claros', description: 'Tonos frescos y veraniegos', examples: ['Colores Pasteles'] },
    { icon: Waves, title: 'Calzado Adecuado', description: 'Cómodo para arena', examples: ['Sandalias elegantes', 'Zapatos planos', 'Sin tacones de aguja', 'Mocasines elegantes'] },
    { icon: Sparkles, title: 'Accesorios', description: 'Detalles que brillan', examples: ['Flores naturales', 'Sombreros elegantes'] },
  ];

  const avoidItems = [
    'Vestidos blancos (solo para la novia)',
    'El color verde lima es solo para las damas de honor',
    'El color azul cielo es solo para los caballeros de honor',
    'Tacones muy altos',
    'Ropa completamente negra',
    'Trajes muy formales',
  ];

  const headerProgress = Math.max(0, Math.min(1, scrollProgress / 0.4));
  const buttonsProgress = Math.max(0, Math.min(1, (scrollProgress - 0.08) / 0.25));

  return (
    <section
      ref={sectionRef}
      className="snap-section relative min-h-screen py-16 md:py-24 overflow-visible flex items-center justify-center"
    >
      <div className="section-content w-full px-6 flex flex-col justify-center">
        <div
          className="section-bg-image absolute inset-0 z-0"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
        <div className="absolute inset-0 z-[1]" style={{ backgroundColor: SECTION_OVERLAY }} />
        <div className="relative z-10 w-full max-w-5xl mx-auto">
          <div
            className="text-center mb-16 transition-all duration-700 ease-out"
            style={{
              opacity: headerProgress,
              transform: `translateY(${24 * (1 - headerProgress)}px)`,
            }}
          >
            <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
              CÓDIGO DE VESTIMENTA
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-4"
              style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
            >
              Formal de playa: elegancia con comodidad junto al mar
            </p>
            <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
          </div>

          <div
            className="flex flex-col sm:flex-row justify-center gap-4 mb-10 transition-all duration-700 ease-out"
            style={{
              opacity: buttonsProgress,
              transform: `translateY(${20 * (1 - buttonsProgress)}px)`,
            }}
          >
            <a
              href={PINTEREST_IDEAS_DAMAS}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border transition-colors hover:opacity-90"
              style={{
                borderColor: 'var(--chartreuse)',
                color: 'var(--emerald)',
                backgroundColor: CARD_BG,
                fontFamily: 'var(--font-serif)',
              }}
            >
              <ExternalLink className="w-4 h-4 shrink-0" />
              <span>Ideas para ellas</span>
            </a>
            <a
              href={PINTEREST_IDEAS_CABALLEROS}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border transition-colors hover:opacity-90"
              style={{
                borderColor: 'var(--chartreuse)',
                color: 'var(--emerald)',
                backgroundColor: CARD_BG,
                fontFamily: 'var(--font-serif)',
              }}
            >
              <ExternalLink className="w-4 h-4 shrink-0" />
              <span>Ideas para ellos</span>
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {dressItems.map((item, index) => {
              const Icon = item.icon;
              const stagger = index * 0.06;
              const cardProgress = Math.max(0, Math.min(1, (scrollProgress - 0.2 - stagger) / 0.35));
              const opacity = cardProgress;
              const translateY = 28 * (1 - cardProgress);
              return (
                <div
                  key={item.title}
                  className="rounded-xl p-6 border backdrop-blur-sm min-h-0 overflow-visible transition-all duration-700 ease-out"
                  style={{
                    borderColor: 'rgba(186, 159, 56, 0.25)',
                    backgroundColor: CARD_BG,
                    opacity,
                    transform: `translateY(${translateY}px)`,
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4 border shrink-0"
                    style={{
                      backgroundColor: 'var(--pale-lime)',
                      borderColor: 'var(--chartreuse)',
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: 'var(--emerald)' }} />
                  </div>
                  <h3 className="text-editorial-title text-sm tracking-[0.12em] mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                    {item.title.toUpperCase()}
                  </h3>
                  <p className="text-sm mb-4 break-words overflow-visible" style={{ color: 'var(--emerald)', opacity: 0.75 }}>
                    {item.description}
                  </p>
                  <ul className="space-y-1 overflow-visible">
                    {item.examples.map((example, i) => (
                      <li key={i} className="text-sm flex items-start break-words" style={{ color: 'var(--emerald)', opacity: 0.85 }}>
                        <span className="mr-2" style={{ color: 'var(--chartreuse)' }}>•</span>
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div
            className="max-w-3xl mx-auto mb-14 transition-all duration-700 ease-out"
            style={{
              opacity: Math.max(0, Math.min(1, (scrollProgress - 0.55) / 0.3)),
              transform: `translateY(${28 * (1 - Math.min(1, (scrollProgress - 0.55) / 0.3))}px)`,
            }}
          >
            <div
              className="rounded-xl p-8 border overflow-visible"
              style={{
                backgroundColor: CARD_BG,
                borderColor: 'rgba(186, 159, 56, 0.3)',
              }}
            >
              <h3 className="text-editorial-title text-xl tracking-[0.2em] mb-2 text-center">
                EVITA
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {avoidItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--pale-lime)', opacity: 0.9 }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--chartreuse)' }} />
                    <span className="text-sm" style={{ color: 'var(--emerald)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="text-center transition-all duration-700 ease-out"
            style={{
              opacity: Math.max(0, Math.min(1, (scrollProgress - 0.75) / 0.25)),
              transform: `translateY(${20 * (1 - Math.min(1, (scrollProgress - 0.75) / 0.25))}px)`,
            }}
          >
            <h4 className="text-editorial-meta text-sm tracking-[0.2em] mb-6">
              PALETA SUGERIDA
            </h4>
            <div className="flex justify-center gap-6 flex-wrap">
              {PALETTE_SWATCHES.map((color) => (
                <div
                  key={color}
                  className="w-16 h-16 rounded-full border shrink-0"
                  style={{ backgroundColor: color, borderColor: 'rgba(186, 159, 56, 0.4)' }}
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
